<template>
  <div class="portal-page auth-page signup-page" :class="{ 'signup-page--mall': isMallSignup }">
    <div class="portal-page__inner auth-page__shell">
      <section v-if="signupDone" class="signup-done" aria-live="polite">
        <span class="signup-done__mark" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M4 12.5l5.5 5.5L20 7" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </span>
        <p class="signup-done__eyebrow">회원가입 완료</p>
        <h1 class="signup-done__title">가입을 축하드립니다</h1>
        <p class="signup-done__desc">
          <strong>{{ doneUserId }}</strong> 님, 함우리몰을 바로 이용할 수 있습니다.
          배송지와 연락처는 처음 주문할 때 받습니다.
        </p>
        <dl class="signup-done__meta">
          <div class="signup-done__row">
            <dt>로그인 아이디</dt>
            <dd>{{ doneUserId }}</dd>
          </div>
        </dl>
        <div class="signup-done__actions">
          <router-link v-if="isLoggedIn" to="/mall" class="btn-primary">함우리몰 둘러보기</router-link>
          <router-link v-else :to="{ name: 'login', query: { joined: '1' } }" class="btn-primary">
            로그인하기
          </router-link>
          <router-link to="/" class="btn-secondary">홈으로</router-link>
        </div>
      </section>

      <template v-else>
        <header class="auth-page__head">
          <h1 class="auth-page__title">회원가입</h1>
        </header>

        <form class="auth-page__panel auth-form signup-form" @submit.prevent="onSubmit">
          <div class="auth-form__body">
            <section class="auth-form__section">
              <div class="auth-form__field">
                <span>아이디</span>
                <div class="auth-form__field-row">
                  <input
                    v-model="form.userId"
                    type="text"
                    autocomplete="username"
                    placeholder="영문, 숫자 (4~20자)"
                    maxlength="20"
                    autocapitalize="none"
                    autocorrect="off"
                    spellcheck="false"
                    :class="userIdInputState"
                    @input="onUserIdInput"
                    @focus="onUserIdFocus"
                    @blur="onUserIdBlur"
                  />
                  <button
                    type="button"
                    class="auth-form__inline-btn"
                    :class="{ 'auth-form__inline-btn--done': userIdChecked }"
                    :disabled="!isUserIdFormatValid(form.userId) || userIdChecked"
                    @click="checkUserId"
                  >
                    {{ userIdChecked ? '확인됨' : '확인' }}
                  </button>
                </div>
                <p
                  v-if="userIdMessage"
                  class="auth-form__msg"
                  :class="userIdChecked ? 'auth-form__msg--ok' : 'auth-form__msg--warn'"
                >
                  {{ userIdMessage }}
                </p>
                <p v-else class="auth-form__hint">로그인할 때 쓸 아이디입니다. 한글은 안 됩니다.</p>
              </div>

              <label class="auth-form__field">
                <span>비밀번호</span>
                <div class="signup-secret">
                  <input
                    v-model="form.password"
                    :type="showPassword ? 'text' : 'password'"
                    autocomplete="new-password"
                    placeholder="8자 이상"
                    :class="passwordInputState"
                  />
                  <button type="button" class="signup-secret__toggle" @click="showPassword = !showPassword">
                    {{ showPassword ? '숨기기' : '보기' }}
                  </button>
                </div>
                <ul class="signup-pw-check" aria-label="비밀번호 조건">
                  <li :class="{ 'signup-pw-check__ok': passwordLongEnough }">8자 이상</li>
                  <li :class="{ 'signup-pw-check__ok': passwordHasLetter }">영문</li>
                  <li :class="{ 'signup-pw-check__ok': passwordHasDigit }">숫자</li>
                  <li :class="{ 'signup-pw-check__ok': passwordHasSpecial }">특수문자 !@#</li>
                </ul>
              </label>

              <label class="auth-form__field">
                <span>비밀번호 다시 입력</span>
                <div class="signup-secret">
                  <input
                    v-model="form.passwordConfirm"
                    :type="showPassword ? 'text' : 'password'"
                    autocomplete="new-password"
                    :placeholder="passwordReady ? '방금 만든 비밀번호를 다시 적어 주세요' : '비밀번호 기준을 먼저 충족해 주세요'"
                    :disabled="!passwordReady"
                    :class="passwordConfirmInputState"
                  />
                  <button type="button" class="signup-secret__toggle" @click="showPassword = !showPassword">
                    {{ showPassword ? '숨기기' : '보기' }}
                  </button>
                </div>
                <p
                  v-if="form.passwordConfirm"
                  class="auth-form__msg"
                  :class="passwordMatched ? 'auth-form__msg--ok' : 'auth-form__msg--warn'"
                >
                  {{ passwordMatched ? '비밀번호가 같습니다.' : '비밀번호가 다릅니다. 다시 봐 주세요.' }}
                </p>
              </label>
            </section>

            <TermsAgreementSection :model-value="agreements" @update:model-value="onAgreementsUpdate" />
          </div>

          <p v-if="errorMessage" class="signup-step-error" role="alert">{{ errorMessage }}</p>

          <div class="signup-nav">
            <button type="submit" class="btn-primary signup-nav__next" :disabled="isSubmitting">
              {{ isSubmitting ? '가입하는 중...' : '가입하기' }}
            </button>
          </div>
        </form>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import TermsAgreementSection, { type AgreementState } from '@/components/auth/TermsAgreementSection.vue'
import { readSignupDone, saveSignupDone } from '@/constants/authPrefill'
import { clearOAuthSignup } from '@/constants/oauthSignup'
import { useMemberAuth } from '@/composables/useMemberAuth'
import { checkMemberUserId, signUpMember } from '@/api/member'
import { saveAutoLogin } from '@/constants/autoLogin'
import {
  collectSignUpValidationErrors,
  inputValidationState,
  isPasswordConfirmValid,
  isPasswordValid,
  isUserIdFormatValid,
  sanitizeUserIdInput,
  USER_ID_PATTERN,
} from '@/utils/formValidation'

const route = useRoute()
const router = useRouter()
const { isLoggedIn, setMember } = useMemberAuth()

const signupDone = ref(false)
const doneUserId = ref('')
const showPassword = ref(false)
const skipLeaveGuard = ref(false)
const isSubmitting = ref(false)
const errorMessage = ref('')
const confirmedUserId = ref('')
const userIdMessage = ref('')

const form = reactive({
  userId: '',
  password: '',
  passwordConfirm: '',
})

const agreements = reactive<AgreementState>({
  age: false,
  terms: false,
  privacy: false,
  marketing: false,
})

function onAgreementsUpdate(next: AgreementState) {
  agreements.age = next.age
  agreements.terms = next.terms
  agreements.privacy = next.privacy
  agreements.marketing = next.marketing
}

const isMallSignup = computed(() => route.meta.layout === 'mall')

const userIdChecked = computed(
  () => confirmedUserId.value !== '' && form.userId === confirmedUserId.value,
)
const passwordLongEnough = computed(() => form.password.length >= 8)
const passwordHasLetter = computed(() => /[A-Za-z]/.test(form.password))
const passwordHasDigit = computed(() => /\d/.test(form.password))
const passwordHasSpecial = computed(() => /[^A-Za-z0-9]/.test(form.password))
const passwordReady = computed(() => isPasswordValid(form.password))
const passwordMatched = computed(() => isPasswordConfirmValid(form.passwordConfirm, form.password))
const userIdInputState = computed(() =>
  inputValidationState(form.userId, isUserIdFormatValid(form.userId) && userIdChecked.value),
)
const passwordInputState = computed(() => inputValidationState(form.password, isPasswordValid(form.password)))
const passwordConfirmInputState = computed(() =>
  inputValidationState(form.passwordConfirm, passwordMatched.value),
)
const hasDraft = computed(
  () => Boolean(form.userId || form.password || form.passwordConfirm || agreements.terms),
)

watch(passwordReady, (ready) => {
  if (!ready) form.passwordConfirm = ''
})

const showSignupDone = (userId: string) => {
  skipLeaveGuard.value = true
  doneUserId.value = userId
  signupDone.value = true
}

function onUserIdInput() {
  form.userId = sanitizeUserIdInput(form.userId)
  if (form.userId !== confirmedUserId.value) {
    confirmedUserId.value = ''
  }
}

function onUserIdFocus() {
  if (form.userId !== confirmedUserId.value) {
    userIdMessage.value = ''
  }
}

function onUserIdBlur() {
  if (isUserIdFormatValid(form.userId) && !userIdChecked.value) {
    void checkUserId()
  }
}

const checkUserId = async () => {
  if (!USER_ID_PATTERN.test(form.userId)) {
    userIdMessage.value = '아이디 형식이 올바르지 않습니다.'
    confirmedUserId.value = ''
    return
  }

  try {
    const result = await checkMemberUserId(form.userId)
    if (result.available) {
      confirmedUserId.value = form.userId
      userIdMessage.value = '사용할 수 있는 아이디입니다.'
      return
    }
    confirmedUserId.value = ''
    userIdMessage.value = '이미 쓰고 있는 아이디입니다. 다른 아이디를 적어 주세요.'
  } catch (error) {
    confirmedUserId.value = ''
    userIdMessage.value = error instanceof Error ? error.message : '아이디 확인에 실패했습니다.'
  }
}

onBeforeRouteLeave(() => {
  if (skipLeaveGuard.value || !hasDraft.value) {
    return true
  }
  return window.confirm('지금까지 적은 내용이 사라집니다.\n다른 화면으로 나가시겠습니까?')
})

const onBeforeUnload = (event: BeforeUnloadEvent) => {
  if (skipLeaveGuard.value || !hasDraft.value) {
    return
  }
  event.preventDefault()
  event.returnValue = ''
}

onMounted(() => {
  window.addEventListener('beforeunload', onBeforeUnload)
  clearOAuthSignup()

  if (route.query.done === '1') {
    const snapshot = readSignupDone()
    if (snapshot) {
      showSignupDone(snapshot.userId)
    }
  }
})

onUnmounted(() => {
  window.removeEventListener('beforeunload', onBeforeUnload)
})

const onSubmit = async () => {
  errorMessage.value = ''
  const errors = collectSignUpValidationErrors({
    form: {
      ...form,
      name: '',
      email: '',
      phone: '',
      orgName: '',
      orgCode: '',
      bizGubun: '',
      bizNum: '',
      bizCert: '',
      zipcode: '',
      address: '',
      addressDetail: '',
      refundBank: '',
      refundAccount: '',
      refundHolder: '',
    },
    userIdChecked: userIdChecked.value,
    orgCodeChecked: true,
    emailVerified: true,
    phoneVerified: true,
    agreements,
  })
  if (errors.length) {
    errorMessage.value = errors[0]
    return
  }

  if (isSubmitting.value) {
    return
  }
  isSubmitting.value = true

  try {
    const created = await signUpMember({
      userId: form.userId,
      password: form.password,
      passwordConfirm: form.passwordConfirm,
      agreements: { ...agreements },
    })

    if (created.member) {
      saveAutoLogin('member', true)
      setMember(created.member)
    }
    saveSignupDone({
      name: form.userId,
      userId: form.userId,
    })
    showSignupDone(created.userId || form.userId)
    await router.replace({ query: { ...route.query, done: '1' } })
    await nextTick()
    window.scrollTo({ top: 0, behavior: 'smooth' })
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '회원가입에 실패했습니다.'
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style lang="scss" src="./SignUpPage.scss"></style>
